import axios from "axios";
import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import AlertComponent from "../../components/AlertComponent";
import Loading from "../../components/Loading";

const TIPO_TOKEN_RESET_SENHA = 2;

export default function ResetSenha() {
  const { token } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [tokenValido, setTokenValido] = useState(false);
  const [tokenInfo, setTokenInfo] = useState(null);

  const [senha, setSenha] = useState("");
  const [confirmarSenha, setConfirmarSenha] = useState("");
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    validarToken();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const validarToken = async () => {
    setLoading(true);
    try {
      const url = `${process.env.REACT_APP_API}/auth/token/${TIPO_TOKEN_RESET_SENHA}/${encodeURIComponent(token)}`;
      const res = await axios.get(url);

      // Seu Response padrão: { success, data, message }
      if (!res?.data?.success) {
        setTokenValido(false);
        setTokenInfo(null);
        return;
      }

      setTokenValido(true);
      setTokenInfo(res.data.data); // opcional: expiraEm, usado, etc.
    } catch (err) {
      setTokenValido(false);
      setTokenInfo(null);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (senha.length < 6) {
      AlertComponent("Erro", "A senha deve ter pelo menos 6 caracteres.", { icon: "error" });
      return;
    }

    if (senha !== confirmarSenha) {
      AlertComponent("Erro", "As senhas não coincidem.", { icon: "error" });
      return;
    }

    setSalvando(true);
    try {
      await axios.post(
        `${process.env.REACT_APP_API}/auth/reset-senha/${encodeURIComponent(token)}`,
        { senha }
      );

      AlertComponent("Sucesso", "Senha alterada com sucesso! Faça login novamente.", { icon: "success" });
      navigate("/login");
    } catch (error) {
      const mensagem = error?.response?.data?.message || error?.message || "Erro ao resetar senha";
      AlertComponent("Erro", mensagem, { icon: "error" });
    } finally {
      setSalvando(false);
    }
  };

  if (loading) return <Loading />;

  if (!tokenValido) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="w-full max-w-md bg-white rounded-xl shadow-lg p-8">
          <h1 className="text-2xl font-semibold text-center text-gray-800 mb-2">
            Link inválido
          </h1>
          <p className="text-sm text-center text-gray-500 mb-6">
            Este link de redefinição é inválido, expirou ou já foi utilizado.
          </p>

          <div className="flex flex-col gap-3">
            <Link
              to="/esqueci-senha"
              className="w-full text-center bg-slate-900 hover:bg-slate-800 text-white py-2.5 rounded-md font-medium transition"
            >
              Solicitar novo link
            </Link>

            <Link
              to="/login"
              className="w-full text-center border border-gray-300 hover:bg-gray-50 py-2.5 rounded-md font-medium transition"
            >
              Voltar para login
            </Link>
          </div>

          <div className="mt-8 text-center text-xs text-gray-400">
            © {new Date().getFullYear()} Quality Digital
          </div>
        </div>
      </div>
    );
  }

  // Token válido
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100">
      <div className="w-full max-w-md bg-white rounded-xl shadow-lg p-8">
        <h1 className="text-2xl font-semibold text-center text-gray-800 mb-2">
          Redefinir senha
        </h1>
        <p className="text-sm text-center text-gray-500 mb-8">
          Defina uma nova senha para sua conta.
        </p>

        {tokenInfo?.expiraEm && (
          <p className="text-xs text-center text-slate-500 mb-6">
            Link expira em:{" "}
            {new Intl.DateTimeFormat("pt-BR", {
              day: "2-digit",
              month: "2-digit",
              year: "numeric",
              hour: "2-digit",
              minute: "2-digit",
              second: "2-digit",
              timeZone: "America/Sao_Paulo",
            }).format(new Date(tokenInfo.expiraEm))}
          </p>
        )}

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nova senha
            </label>
            <input
              type="password"
              placeholder="Mínimo 6 caracteres"
              value={senha}
              onChange={(e) => setSenha(e.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm
                         focus:outline-none focus:ring-2 focus:ring-slate-600"
              required
              minLength={6}
              disabled={salvando}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Confirmar nova senha
            </label>
            <input
              type="password"
              placeholder="Repita a senha"
              value={confirmarSenha}
              onChange={(e) => setConfirmarSenha(e.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm
                         focus:outline-none focus:ring-2 focus:ring-slate-600"
              required
              minLength={6}
              disabled={salvando}
            />
          </div>

          <button
            type="submit"
            disabled={salvando}
            className="w-full bg-slate-900 hover:bg-slate-800 disabled:opacity-60 text-white py-2.5 rounded-md font-medium transition"
          >
            {salvando ? "Salvando..." : "Salvar nova senha"}
          </button>
        </form>

        <div className="mt-6 flex justify-between items-center text-sm">
          <Link to="/login" className="text-slate-600 hover:text-slate-900 hover:underline">
            Voltar para login
          </Link>
        </div>

        <div className="mt-8 text-center text-xs text-gray-400">
          © {new Date().getFullYear()} Gerenciador de Certificados
        </div>
      </div>
    </div>
  );
}